import classNames from 'classnames';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Icons } from 'src/assets/fonts/Icons/icons';
import theme from 'src/constants/styles';
import { localeStore } from 'src/mobx/localesStore';
import Icon from 'src/view/components/Controls/Icon/Icon';
import { allowedImageTypes } from 'src/view/components/Controls/Upload/constants';
import { uploadAllowedFileTypes, uploadMaxFileSize } from 'src/view/components/Controls/Upload/helpers';
import { IFile, IUploadProps } from 'src/view/components/Controls/Upload/types';
import Upload from 'src/view/components/Controls/Upload/Upload';
import { IValidation } from '~/types/main';
import './style.scss';

interface IProps {
  type: IUploadProps['type'];
  image?: string;
  title: string;
  uploadImage: (file: any) => void;
  hints?: string[];
  validation?: IValidation;
  required?: boolean;
  small?: boolean;
  maxSize: number; // in bytes
  maxResolution?: string;
  reqResolution?: string;
  allowedTypes?: string[];
  className?: string;
  uploadWrapperClassName?: string;
}

const ImageBlock: FC<IProps> = ({ type, small = false, hints = [], ...props }: IProps): JSX.Element => {
  const [cropMode, setCropMode] = useState<boolean>(false);

  useEffect(() => {
    if (props.image) setCropMode(false);
  }, [props.image]);

  const onImageChange = useCallback((file: IFile) => {
    setCropMode(Boolean(file.result));
  }, []);

  const onResetImage = useCallback(() => {
    props.uploadImage('');
  }, []);

  const isInvalid = useMemo((): boolean => Boolean(props.validation && !props.validation.isValid), [props.validation?.isValid]);

  const allowedFormats = useMemo((): string[] => {
    return props.allowedTypes || (small ? [...allowedImageTypes, 'image/x-icon'] : allowedImageTypes);
  }, [small]);

  const uploadClassName = useMemo((): string => {
    return classNames('image-block__upload-wrapper', props.uploadWrapperClassName, {
      'image-block__upload-wrapper_small': small,
      'image-block__upload-image_cover': !small,
      'image-block__upload-image_scale-down': small && type === 'favicon',
      'image-block__upload-image_contain': small && type !== 'favicon',
      'input-error': isInvalid,
    });
  }, [type, small, isInvalid]);

  return (
    <div data-testid="testid-image-block" className={classNames('image-block', props.className, { 'image-block_small': small })}>
      <p className="image-block__title">{localeStore.t(props.title)}</p>

      <div className="d-flex flex-row align-items-center">
        <p className="image-block__max-size image-block__max-size_label">{localeStore.t('max_size')}</p>
        <p className="image-block__max-size image-block__max-size_value">{uploadMaxFileSize(props.maxSize)}</p>
      </div>
      {props.maxResolution || props.reqResolution ? (
        <div className="d-flex flex-row align-items-center">
          <p className="image-block__max-size image-block__max-size_label">
            {localeStore.t(props.maxResolution ? 'max_resolution' : 'required_resolution')}
          </p>
          <p className="image-block__max-size image-block__max-size_value">{props.maxResolution || props.reqResolution}</p>
        </div>
      ) : null}
      <div className="d-flex flex-row align-items-center">
        <p className="image-block__max-size image-block__max-size_label">{localeStore.t('format')}</p>
        <p className="image-block__max-size image-block__max-size_value">{'' + String(uploadAllowedFileTypes(allowedFormats))}</p>
      </div>

      {hints.length ? (
        <ul className="image-block__hints">
          {hints.map((hint) => (
            <li className="family-regular text-size-12">{localeStore.t(hint)}</li>
          ))}
        </ul>
      ) : null}

      <div className="pos-relative">
        <Upload
          image={props.image}
          onChange={onImageChange}
          changeFile={props.uploadImage}
          allowedFileTypes={allowedFormats}
          maxFileSize={props.maxSize}
          smallValidationBlock={small}
          apiUrl="v3/settings/consumer-channels/images"
          customType={type}
          className={uploadClassName}
        >
          <div className="image-block__upload-container">
            <div className="image-block__upload-square-container">
              <span className="image-block__upload-square image-block__upload-square_blue" />
              <span className="image-block__upload-square image-block__upload-square_gray" />
              <Icon name={Icons.Hand} size={small ? 24 : 40} color={theme.colors.gray2} className="image-block__upload-square-icon" />
            </div>
            <p className={classNames('image-block__upload-text', { required: props.required })}>{localeStore.t('upload_image')}</p>
          </div>
        </Upload>
        {props.image && !cropMode ? (
          <div onClick={onResetImage} className="image-block__icon-reset">
            <Icon name={Icons.Close} size={10} color={theme.colors.gray2} />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ImageBlock;
